import {
	useState,
	useRef,
	useEffect,
	useCallback,
	useMemo,
	forwardRef,
	useImperativeHandle,
} from 'react';
import { LuSend as Send } from 'react-icons/lu';
import { Container, Card, Button, Form, Col } from 'react-bootstrap';
import './opportunityChat.scss';
import { OpportunityLogEntryDTO } from 'src/api/models/Opportunities/OpportunityLogEntryDTO';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import SpinnerButton from 'src/components/SpinnerButton/SpinnerButton';
import * as Yup from 'yup';
import { useAuth } from 'src/hooks/useAuth';
import * as Utils from './opportunityChatUtils';
import { PinnedMessagesAlert } from './PinnedMessagesAlert';
import { TextMessage, Message } from './TextMessage';
import { RoleEnum } from 'src/api';

type OpportunityChatProps = {
	logs: OpportunityLogEntryDTO[];
	showLastMessage?: boolean;
	addMessage: any;
	updateMessage: any;
	isLoading?: boolean;
	isAdding?: boolean;
	isUpdating?: boolean;
	className?: string;
	dealer?: string;
};

export type OpportunityChatHandle = {
	scrollToBottom: () => void;
	scrollToMessage: (messageId: string, executor?: string) => void;
};

const OpportunityChat = forwardRef<OpportunityChatHandle, OpportunityChatProps>(
	(
		{
			logs,
			showLastMessage = false,
			addMessage,
			updateMessage,
			isLoading,
			isAdding,
			isUpdating,
			className,
			dealer,
		},
		ref
	) => {
		const { user, userRole } = useAuth();

		const [messages, setMessages] = useState<Message[]>([]);
		const [pinnedMessages, setPinnedMessages] = useState<Message[]>([]);
		const messagesContainerRef = useRef<HTMLDivElement>(null);

		useEffect(() => {
			const mappedMessages: Message[] = (logs || []).map((log) => ({
				id: log.time,
				sender: log.author,
				text: log.message,
				time: log.time,
				isOwn: user.username === log.author,
				avatar: Utils.getAvatarInitials(log.author),
				pinned: log.pinned,
			}));
			setMessages(mappedMessages);

			const mappedPinnedMessages: Message[] = mappedMessages.filter(
				(log) => log.pinned
			);
			setPinnedMessages(mappedPinnedMessages);
		}, [logs, user?.username]);

		const [pulsatingMessageId, setPulsatingMessageId] = useState<
			string | null | undefined
		>();
		const messagesEndRef = useRef<HTMLDivElement>(null);
		const messageRefs = useRef<{ [key: string]: HTMLDivElement }>({});

		const { t } = useTranslation();
		const initRef = useRef(false);

		const sendMessageSchema = Yup.object({
			message: Yup.string().required(t('insert_a_message')),
		});

		const scrollToBottom = () => {
			if (messagesContainerRef.current) {
				messagesContainerRef.current.scrollTop =
					messagesContainerRef.current.scrollHeight;
			}
		};

		const scrollToMessage = useCallback(
			(messageId: string, executor?: string) => {
				if (executor === 'pinned') {
					const messageElement = messageRefs.current[messageId];
					if (messageElement) {
						messageElement.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
						});

						setPulsatingMessageId(messageId);
						setTimeout(() => {
							setPulsatingMessageId(null);
						}, 1500);
					}
				}
				const messageElement = messageRefs.current[messageId];
				if (messageElement && messagesContainerRef.current) {
					const containerTop = messagesContainerRef.current.offsetTop;
					const elementTop = messageElement.offsetTop;
					const scrollTop =
						elementTop -
						containerTop -
						messagesContainerRef.current.clientHeight / 2;

					messagesContainerRef.current.scrollTop = scrollTop;

					setPulsatingMessageId(messageId);
					setTimeout(() => {
						setPulsatingMessageId(null);
					}, 1500);
				}
			},
			[]
		);

		useEffect(() => {
			if (!initRef.current) {
				initRef.current = true;
				if (showLastMessage) scrollToBottom();
				return;
			}
			// scrollToBottom();
		}, [messages, showLastMessage]);

		const handleTogglePin = async (messageId: string) => {
			const messageToUpdate = logs.find((m) => m.time === messageId);
			if (!messageToUpdate) return;

			const updatedLog = {
				...messageToUpdate,
				pinned: !messageToUpdate.pinned,
			};

			await updateMessage(updatedLog);
		};

		const handleUnpinMessage = async (messageId: string) => {
			const messageToUpdate = logs.find((m) => m.time === messageId);
			if (!messageToUpdate) return;
			const updatedLog = { ...messageToUpdate, pinned: false };
			await updateMessage(updatedLog);
		};

		const handleUnpinAllMessages = async () => {
			const messageListToUnpin: OpportunityLogEntryDTO[] = pinnedMessages
				.map((p) => logs.find((m) => p.id === m.time))
				.filter((log): log is OpportunityLogEntryDTO => Boolean(log));

			const updatePromises = messageListToUnpin.map(async (message) => {
				const updatedLog = { ...message, pinned: false };
				await updateMessage(updatedLog);
			});

			await Promise.all(updatePromises);
		};

		const handleSendMessage = (
			values: { message: string },
			{ resetForm }: { resetForm: () => void }
		) => {
			if (values.message.trim()) {
				addMessage({ message: values.message });
				resetForm();
			}
		};

		const canUserPinMessages = useMemo(
			() =>
				[
					RoleEnum.ADMIN,
					RoleEnum.SALES,
					RoleEnum.SALES_DEALER,
					RoleEnum.SALES_INTERNAL,
					RoleEnum.BACKOFFICE,
					RoleEnum.MASTER,
					RoleEnum.AREA_MANAGER,
				].includes(userRole),
			[userRole]
		);

		const isSubmitDisabled = (isSubmitting) =>
			[isSubmitting, isLoading, isAdding, isUpdating].some(Boolean);

		const chatTitle = useMemo(() => {
			const fragments = [t('communications'), dealer];
			return fragments.filter(Boolean).join(' - ');
		}, [dealer, t]);

		useImperativeHandle(ref, () => ({
			scrollToBottom,
			scrollToMessage,
		}));

		return (
			<Container
				fluid
				className={className || ''}
				style={{ margin: '0 auto', height: '100%' }}
			>
				<Card style={{ height: '100%' }} className="upfront">
					{/* Pinned messages */}
					{pinnedMessages.length > 0 && (
						<PinnedMessagesAlert
							pinnedMessages={pinnedMessages}
							scrollToMessage={(messageId) => {
								scrollToMessage(messageId, 'pinned');
							}}
							handleUnpinMessage={handleUnpinMessage}
							canPinMessages={canUserPinMessages}
							handleUnpinAllMessages={(e) => {
								e.stopPropagation();
								handleUnpinAllMessages();
							}}
						/>
					)}

					{/* Chat header */}
					<Card.Header className="bg-white border-bottom py-3">
						<Card.Subtitle className="mb-2 mt-2 d-flex align-items-center justify-content-between fs-5">
							<div className="fw-bold">{chatTitle}</div>
						</Card.Subtitle>
					</Card.Header>

					{/* Messages container */}
					<Card.Body
						ref={messagesContainerRef}
						className="bg-light overflow-auto chat-messages-container"
						style={{ height: '400px', padding: '16px' }}
					>
						{messages.map((message) => (
							<TextMessage
								key={message.id}
								message={message}
								pinnedMessages={pinnedMessages}
								canPinMessages={canUserPinMessages}
								handleTogglePin={handleTogglePin}
								messageRefs={messageRefs}
								pulsatingMessageId={pulsatingMessageId}
							/>
						))}
						<div ref={messagesEndRef} style={{ height: '100px' }} />
					</Card.Body>

					{/* Message input */}
					<Card.Footer className="bg-white border-top py-2">
						<Formik
							initialValues={{ message: '' }}
							enableReinitialize={true}
							onSubmit={handleSendMessage}
							validationSchema={sendMessageSchema}
						>
							{({
								values,
								handleChange,
								handleBlur,
								handleSubmit,
								touched,
								isSubmitting,
								errors,
							}) => (
								<Form onSubmit={handleSubmit}>
									<Col md={12}>
										<Form.Group className="mb-3 mt-3">
											<div className="d-flex">
												<Form.Control
													as="textarea"
													onChange={handleChange}
													onBlur={handleBlur}
													name="message"
													rows={3}
													value={values.message}
													type="text"
													style={{
														borderRadius: '6px',
														backgroundColor: '#FFFFFF',
													}}
													placeholder={`${t('add_a_comment')}...`}
													disabled={isSubmitDisabled(isSubmitting)}
													className={
														'bg-light rounded-pill custom-input-control me-2'
													}
												/>
												{errors.message && touched.message && (
													<div className="invalid-feedback">
														{errors.message}
													</div>
												)}
												<Button
													type="submit"
													style={{
														width: '40px',
														height: '40px',
														flexShrink: 0,
													}}
													className="btn-my-custom-2 align-self-end rounded-circle d-flex align-items-center justify-content-center ms-2"
													disabled={isSubmitDisabled(isSubmitting)}
												>
													<SpinnerButton
														label={<Send size={18} />}
														busy={isSubmitDisabled(isSubmitting)}
													/>
												</Button>
											</div>
										</Form.Group>
									</Col>
								</Form>
							)}
						</Formik>
					</Card.Footer>
				</Card>
			</Container>
		);
	}
);

export default OpportunityChat;
